<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import RichTextField from "@/components/RichTextField.vue";
import { uploadImage } from "@/lib/uploads";
import { toDateTimeLocal } from "@/lib/date";
import { fetchUsers } from "@/features/users/api";
import { fetchReminder, addReminder, editReminder, fetchTopics, uploadReminderFile } from "./api";

const DEFAULT_IMG = "https://firebasestorage.googleapis.com/v0/b/aspial.appspot.com/o/announcements%2Faspial.jpg?alt=media&token=ea877aa0-2ec4-4caa-9342-3280ceafb2fc";
const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);

const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref(""), uploadingFile = ref(false), usersLoading = ref(false);

const timingItems = [{ title: "Task Reminder", value: "Task Reminder" }, { title: "Recurring Reminder", value: "Recurring Reminder" }];
const recurrenceItems = ["Weekdays", "Daily", "Weekly", "Monthly", "Yearly"];
const dayItems = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const btnLinkItems = ["Kronos", "IHP", "Success Factor", "Concur", "Zendesk", "Campaign", "Videos", "More Options"];

const form = ref({ timing: "", reminderDays: "", startDate: "", endDate: "", recurrence: "", day: "", time: "", notif_title: "", notif_content: "", title: "", content: "", btnTitle: "", btnLink: "", redirectUrl: "", isExternalBrowser: false, imgUrl: "" });
const selectedUsers = ref([]), topics = ref([]), allUsers = ref([]), allTopics = ref([]);

const isRecurring = computed(() => form.value.timing === "Recurring Reminder");
const showDay = computed(() => isRecurring.value && form.value.recurrence !== "Daily" && form.value.recurrence !== "Weekdays");
const imageUploader = (file, p) => uploadImage("messages", file, p);
const contentImageUploader = (file) => uploadImage("messages", file);

onMounted(async () => {
  usersLoading.value = true;
  try {
    const [userList, topicList] = await Promise.all([fetchUsers().catch(() => []), fetchTopics().catch(() => [])]);
    allUsers.value = (Array.isArray(userList) ? userList : []).map(u => ({ ...u, id: u.objectID ?? u._id ?? u.id }));
    allTopics.value = Array.isArray(topicList) ? topicList : [];
  } finally { usersLoading.value = false; }
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const r = await fetchReminder(id.value);
    if (!r) { notFound.value = true; return; }
    if (!r.topic || r.topic.length === 0) selectedUsers.value = r.userGuids || []; else topics.value = r.topic;
    form.value.timing = r.type || "";
    if (r.type === "Task Reminder") form.value.reminderDays = (r.reminderDays ?? "").toString();
    else { form.value.startDate = toDateTimeLocal(r.startDate); form.value.endDate = toDateTimeLocal(r.endDate); form.value.recurrence = r.recurrence || ""; form.value.day = r.day || ""; form.value.time = r.time || ""; }
    form.value.notif_title = r.notif_title ?? ""; form.value.notif_content = r.notif_content ?? ""; form.value.title = r.title ?? ""; form.value.content = r.content ?? ""; form.value.btnTitle = r.btnTitle ?? ""; form.value.btnLink = r.btnLink ?? ""; form.value.redirectUrl = r.redirectUrl ?? ""; form.value.imgUrl = r.imgUrl ?? ""; form.value.isExternalBrowser = !!r.isExternalBrowser;
  } catch { error.value = "Couldn't load the reminder."; }
  finally { loading.value = false; }
});

async function onUrlFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  uploadingFile.value = true; error.value = "";
  try { form.value.redirectUrl = await uploadReminderFile(file); }
  catch { error.value = "Couldn't upload the file."; }
  finally { uploadingFile.value = false; }
}

async function submit() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.timing) { validationError.value = "Choose a reminder type."; return; }
  if (!f.notif_title.trim()) { validationError.value = "Notification Title is required."; return; }
  if (!f.title.trim()) { validationError.value = "Inbox Title is required."; return; }
  const imgUrl = f.imgUrl || DEFAULT_IMG;
  const msgContent = Object.assign({}, selectedUsers.value.map(u => [u]));
  let payload;
  if (isRecurring.value) {
    payload = { imgUrl, notif_title: f.notif_title, notif_content: f.notif_content, title: f.title, content: f.content, startDate: new Date(f.startDate), endDate: new Date(f.endDate), btnTitle: f.btnTitle, redirectUrl: f.redirectUrl, btnLink: f.btnLink, userGuids: selectedUsers.value || [], topic: topics.value || [], recurrence: f.recurrence, day: f.day, time: f.time, msgContent, isExternalBrowser: f.isExternalBrowser, type: f.timing };
  } else {
    payload = { imgUrl, notif_title: f.notif_title, notif_content: f.notif_content, title: f.title, content: f.content, btnTitle: f.btnTitle, redirectUrl: f.redirectUrl, btnLink: f.btnLink, userGuids: selectedUsers.value || [], topic: topics.value || [], reminderDays: String(f.reminderDays).trim().split(",").map(Number), msgContent, isExternalBrowser: f.isExternalBrowser, type: f.timing, startDate: new Date(), endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1000)) };
  }
  saving.value = true;
  try {
    if (isEdit.value) await editReminder({ ...payload, itemId: id.value }); else await addReminder(payload);
    success.value = true; setTimeout(() => router.push({ name: "reminders" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'reminders' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Reminder" : "Create Reminder" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Reminder not found.</VAlert>
    <VCard v-else class="pa-4" max-width="800">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="submit">
        <UploadField v-model="form.imgUrl" kind="image" label="Cover Photo" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="12" md="6"><VSelect v-model="form.timing" label="Reminder Type" :items="timingItems" item-title="title" item-value="value" /></VCol>
          <VCol v-if="form.timing === 'Task Reminder'" cols="12">
            <VTextField v-model="form.reminderDays" label="Days of reminder (comma-separated)" placeholder="e.g. 1, 7, 30" />
          </VCol>
          <template v-if="isRecurring">
            <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="datetime-local" /></VCol>
            <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="datetime-local" /></VCol>
            <VCol cols="12" md="4"><VSelect v-model="form.recurrence" label="Repeat" :items="recurrenceItems" /></VCol>
            <VCol v-if="showDay" cols="12" md="4"><VSelect v-model="form.day" label="Day" :items="dayItems" /></VCol>
            <VCol cols="12" md="4"><VTextField v-model="form.time" label="Time" type="time" /></VCol>
          </template>
          <VCol v-if="form.timing && !isRecurring" cols="12">
            <div class="text-body-2 font-weight-semibold mb-1">Employees</div>
            <VAutocomplete v-model="selectedUsers" label="Select employees" :items="allUsers.map(u => ({ title: u.name || u.display_name, value: u.id }))" item-title="title" item-value="value" multiple chips closable-chips :loading="usersLoading" auto-select-first />
          </VCol>
          <VCol v-if="form.timing && isRecurring" cols="12">
            <VAutocomplete v-model="topics" label="Topics" :items="allTopics.map(t => ({ title: t.topic, value: t.topic }))" item-title="title" item-value="value" multiple chips closable-chips auto-select-first />
          </VCol>
          <VCol cols="12"><VTextField v-model="form.notif_title" label="Notification Title" /></VCol>
          <VCol cols="12"><VTextarea v-model="form.notif_content" label="Notification Content" rows="2" /></VCol>
          <VCol cols="12"><VTextField v-model="form.title" label="Inbox Title" /></VCol>
          <VCol cols="12"><RichTextField v-model="form.content" label="Inbox Content" :image-uploader="contentImageUploader" /></VCol>
          <VCol cols="12" md="4"><VTextField v-model="form.btnTitle" label="Button Title" /></VCol>
          <VCol cols="12" md="4">
            <VSelect v-if="!form.redirectUrl" v-model="form.btnLink" label="Button Link" :items="btnLinkItems" clearable />
          </VCol>
          <VCol v-if="!form.btnLink" cols="12" md="4"><VTextField v-model="form.redirectUrl" label="Button URL" /></VCol>
          <VCol cols="12">
            <div class="text-body-2 font-weight-semibold mb-1">Upload PDF / Image (sets Button URL)</div>
            <input type="file" accept="image/*,application/pdf,video/*" @change="onUrlFile" />
            <span v-if="uploadingFile" class="text-caption ml-2">Uploading…</span>
          </VCol>
          <VCol cols="12"><VSwitch v-model="form.isExternalBrowser" label="Open in External Browser" hide-details /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving" :disabled="uploadingFile">Submit</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'reminders' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
